import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Phone, MapPin, Star, Shield, Clock, Award } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet";
import heroMobileCouple from "@/assets/hero-mobile-couple.jpg";

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Mobility Equipment Supplier Clinton | Affordable Home Elevator & Stairlift</title>
        <meta name="description" content="Mobility Equipment Supplier offering Stairlifts and Home Elevators in Clinton, MO. Professional installation serving Henry County. Licensed & Insured. Call (660) 347-2070." />
      </Helmet>

      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between gap-4 py-3">
            {/* Logo */}
            <a href="/" aria-label="Affordable Home Stairlift Home" className="flex-shrink-0">
              <svg 
                className="h-10 w-auto" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 1211.81 485.82"
                role="img"
                aria-labelledby="logoTitle"
              >
                <title id="logoTitle">Affordable Home Stairlift Logo</title>
                <defs>
                  <clipPath id="clippath">
                    <polygon fill="none" points="1.91 2.49 1209.53 0 1211.81 105.13 0 485.82 1.91 2.49"/>
                  </clipPath>
                </defs>
                <g clipPath="url(#clippath)">
                  <path fill="#149574" d="m243.91,2v34h-24v41l-1,1h-24v41l-1,1h-22v41l-1,1h-24v41l-1,1h-23v41l-1,1h-22v41l-1,1h-22v41l-1,1h-23v41l-1,1h-23v41l-1,1H1.91v42.49l58-.08L288.91,46h823.54c24.74-7.73,31.9-17.4,33-44H243.91Z"/>
                </g>
                <g>
                  <path fill="currentColor" d="m356.71,84.71h67.58l70.22,360.1h-72.34l-14.26-81.84h-127.78l-40.66,81.84h-74.45L356.71,84.71Zm25.87,130.42c-3.39-18.25-4.75-36.81-6.34-54.38h-1.58c-11.7,36.92-52.17,113.16-69.7,148.9h94.51l-16.9-94.51Z"/>
                  <path fill="currentColor" d="m741.62,291.15h-139.39l-24.29,153.65h-72.34l57.55-360.1h72.34l-22.7,142.03h139.39l22.71-142.03h72.34l-57.55,360.1h-72.34l24.29-153.65Z"/>
                  <path fill="currentColor" d="m1029.38,174.99c-2.42-39.26-50.62-53.78-76.56-29.57-14.81,14.27-15.8,41.21-1.05,56.76,7.04,7.92,19.01,15.4,35.9,22.44,57.29,25.48,88.44,55.65,89.23,116.16,1.06,49.86-28.42,92.52-75.51,107.71-84.19,26.17-168.96-9.79-174.24-107.71h73.39c1.76,20.06,7.74,34.15,17.95,42.24,19.26,16.28,55.62,16.64,73.13-1.58,17.94-16.71,18.52-50.43,2.38-69.43-7.75-9.68-22-18.92-42.77-27.72-49.78-22.32-80.71-53.49-81.31-106.13-1.64-62.89,54.12-106.73,114.58-105.07,61.53-1.35,112.94,39.28,116.16,101.9h-71.28Z"/>
                </g>
              </svg>
            </a>

            {/* NAHB Badge */}
            <img src="/images/nahb.jpg" alt="NAHB Certified" className="h-10 hidden md:block flex-shrink-0" />

            {/* Main Navigation - Desktop */}
            <nav className="hidden lg:flex flex-1">
              <ul className="flex items-center gap-1">
                <li>
                  <a href="/" className="block px-3 py-2 text-sm font-medium hover:text-primary transition-colors">
                    Home
                  </a>
                </li>
                <li className="relative group">
                  <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium hover:text-primary transition-colors">
                    Home Elevator
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7,10L12,15L17,10H7Z" />
                    </svg>
                  </button>
                  <ul className="absolute left-0 top-full invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all bg-card border rounded-lg shadow-lg py-2 min-w-[250px] z-50">
                    <li><a href="/home-elevator" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Home Elevator</a></li>
                    <li><a href="/home-elevator-cost-calculator" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Cost Calculator</a></li>
                    <li><a href="/elevator-efficiency" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Energy Efficient</a></li>
                    <li><a href="/elevator-pricing" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Pricing</a></li>
                    <li className="relative group/sub">
                      <button className="flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-accent transition-colors">
                        Convenience
                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M10,17L15,12L10,7V17Z" />
                        </svg>
                      </button>
                      <ul className="absolute left-full top-0 invisible opacity-0 group-hover/sub:visible group-hover/sub:opacity-100 transition-all bg-card border rounded-lg shadow-lg py-2 min-w-[280px] z-50">
                        <li><a href="/elevator-features" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Elevator Features</a></li>
                        <li><a href="/elevator-benefits" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Home Elevator Benefits</a></li>
                        <li><a href="/elevator-how-to" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">How to Use The AHS Elevator</a></li>
                        <li><a href="/elevator-faq" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Frequently Asked Questions</a></li>
                      </ul>
                    </li>
                    <li className="relative group/sub">
                      <button className="flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-accent transition-colors">
                        Installation
                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M10,17L15,12L10,7V17Z" />
                        </svg>
                      </button>
                      <ul className="absolute left-full top-0 invisible opacity-0 group-hover/sub:visible group-hover/sub:opacity-100 transition-all bg-card border rounded-lg shadow-lg py-2 min-w-[200px] z-50">
                        <li><a href="/elevator-for-your-home" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Made for Your Home</a></li>
                        <li><a href="/elevator-our-team" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Our Team</a></li>
                      </ul>
                    </li>
                    <li><a href="/elevator-safety" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Home Elevator Safety</a></li>
                    <li><a href="/elevator-glossary" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Glossary of Terms</a></li>
                    <li><a href="/elevator-reviews" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Customer Reviews</a></li>
                  </ul>
                </li>
                <li className="relative group">
                  <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium hover:text-primary transition-colors">
                    Stair Lift
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7,10L12,15L17,10H7Z" />
                    </svg>
                  </button>
                  <ul className="absolute left-0 top-full invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all bg-card border rounded-lg shadow-lg py-2 min-w-[280px] z-50">
                    <li><a href="/stairlift" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Stair Lifts</a></li>
                    <li><a href="/stair-lift-cost-calculator" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Cost Calculator</a></li>
                    <li><a href="/about-stairlifts" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">About Stair Lifts</a></li>
                    <li><a href="/how-stairlifts-work" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">How Stair Lifts Work</a></li>
                    <li><a href="/stairlift-benefits" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Stair Lift Benefits</a></li>
                    <li><a href="/stairlift-reviews" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Customer Reviews</a></li>
                    <li><a href="/stairlift-faq" className="block px-4 py-2 text-sm hover:bg-accent transition-colors">Frequently Asked Questions</a></li>
                  </ul>
                </li>
                <li>
                  <a href="/wheelchair-lift" className="block px-3 py-2 text-sm font-medium hover:text-primary transition-colors">
                    Wheelchair Lift
                  </a>
                </li>
                <li>
                  <a href="/about-us" className="block px-3 py-2 text-sm font-medium hover:text-primary transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/contact" className="block px-3 py-2 text-sm font-medium hover:text-primary transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </nav>

            {/* Contact Info - Desktop */}
            <div className="hidden lg:flex flex-col gap-0.5 text-right flex-shrink-0">
              <a href="tel:6603472070" className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors justify-end text-sm">
                <Phone className="w-3.5 h-3.5" />
                <span className="font-semibold">(660) 347-2070</span>
              </a>
              <a href="mailto:Mike@AffordableHomeElevator.com" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors justify-end">
                <svg className="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                </svg>
                <span>Mike@AffordableHomeElevator.com</span>
              </a>
            </div>

            {/* Mobile Phone Button */}
            <a href="tel:6603472070" className="flex lg:hidden items-center gap-2 text-primary hover:text-primary/80 transition-colors">
              <Phone className="w-5 h-5" />
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-cover bg-center">
        {/* Desktop Hero Image */}
        <div 
          className="absolute inset-0 hidden md:block bg-cover bg-center"
          style={{ backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/hero-couple.jpg')" }}
        />
        {/* Mobile Hero Image - Optimized */}
        <div 
          className="absolute inset-0 md:hidden bg-cover bg-center"
          style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${heroMobileCouple})` }}
        />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Mobility Equipment Supplier in Clinton, MO
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              As Clinton, MO's trusted mobility equipment supplier, we provide professional stairlift and home elevator installation throughout Henry County. Our team specializes in accessibility solutions that help you age safely and comfortably in your own home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                <a href="tel:6603472070">
                  <Phone className="w-5 h-5 mr-2" />
                  Call (660) 347-2070
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild className="bg-white/10 backdrop-blur border-white text-white hover:bg-white/20">
                <NavLink to="/contact">Get Free Quote</NavLink>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-card border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-semibold">Licensed & Insured</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-6 h-6 text-primary" />
              <span className="font-semibold">Same-Day Service</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-6 h-6 text-primary" />
              <span className="font-semibold">NAHB Certified</span>
            </div>
            <img src="/images/nahb.jpg" alt="NAHB Certified Aging-in-Place Specialist" className="h-12" />
          </div>
        </div>
      </section>

      {/* Elevator Service Section */}
      <section id="elevator-service" className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Elevator Service Clinton, MO</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Our professional home elevator installation and repair services in Clinton ensure safe, reliable multi-story accessibility for your residence. Whether you need a new installation or maintenance on an existing system, our certified technicians provide expert elevator service throughout Henry County with commercial-grade reliability and residential comfort.
            </p>
            
            <Card className="overflow-hidden">
              <img src="/images/elevator-residential.jpg" alt="Residential elevator installation in Clinton" className="w-full h-64 object-cover" loading="lazy" />
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3">Residential Elevators</h3>
                <p className="text-muted-foreground mb-4">
                  Full-service home elevator installation for multi-story accessibility. Starting at $27,995.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Enclosed & open platform options</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Commercial-grade reliability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Serving residential & light commercial</span>
                  </li>
                </ul>
                <div className="flex gap-3">
                  <Button asChild className="flex-1">
                    <NavLink to="/home-elevator">Learn More</NavLink>
                  </Button>
                  <Button asChild variant="outline" className="flex-1">
                    <NavLink to="/home-elevator-cost-calculator">Cost Calculator</NavLink>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Medical Equipment Section */}
      <section id="medical-equipment" className="py-16 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Medical Equipment Supplier Clinton, MO</h2>
            <p className="text-lg text-muted-foreground mb-8">
              As a trusted medical equipment supplier in Clinton, we understand the importance of aging in place safely and affordably. Our mobility solutions, including stairlifts and accessibility equipment, enable seniors and individuals with mobility challenges to maintain their independence at home. With flexible financing options and professional installation, we make essential medical equipment accessible to every family in Henry County.
            </p>
            
            <Card className="overflow-hidden">
              <img src="/images/stairlift-installed.jpg" alt="Medical stairlift equipment installed on residential staircase" className="w-full h-64 object-cover" loading="lazy" />
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3">Stairlifts for Aging in Place</h3>
                <p className="text-muted-foreground mb-4">
                  Straight, curved, and L-shaped stairlift solutions for any staircase configuration. Affordable mobility solutions starting at $2,500 installed.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Battery backup for power outages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Indoor & outdoor models available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Flexible financing options</span>
                  </li>
                </ul>
                <div className="flex gap-3">
                  <Button asChild className="flex-1">
                    <NavLink to="/stair-lifts">Learn More</NavLink>
                  </Button>
                  <Button asChild variant="outline" className="flex-1">
                    <NavLink to="/stair-lift-cost-calculator">Cost Calculator</NavLink>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Local Service Area */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Proudly Serving Clinton & Henry County
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  As your local mobility equipment supplier in Clinton, MO, we provide fast, professional service throughout the region. Our team can reach your home near downtown Clinton, Artesian Park, or surrounding communities in under an hour.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold">Service Areas Include:</p>
                      <p className="text-muted-foreground">Clinton, Warsaw, Windsor, Deepwater, Montrose, and all of Henry County, and more</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-[400px] rounded-lg overflow-hidden border">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d49788.97825985!2d-93.81!3d38.37!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87c456e1c9a3f8e1%3A0x7c6c4b4f4b4f4b4f!2sClinton%2C%20MO%2064735!5e0!3m2!1en!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Clinton, MO Service Area Map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-card">
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "How much does a stairlift cost in Clinton, MO?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "On average, straight stair lifts cost between $2,500 and $5,000, including installation. Curved custom models typically range from $8,500 to $15,000+ depending on staircase complexity. Use our calculator for an exact quote based on your home."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is the cost of a residential elevator vs a stairlift?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Residential elevators are significantly more expensive, often starting at $20,000 to $50,000+ due to required home construction. Stair lifts are a far more affordable alternative for multi-story accessibility in Clinton homes."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How quickly can you install a stairlift in Clinton?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Straight stairlift installations typically take 2-4 hours and can often be scheduled within days. Curved stairlifts require custom rail fabrication (2-3 weeks) followed by a 4-6 hour installation. We serve Clinton and surrounding Henry County communities with prompt, professional service."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you offer financing for stairlifts?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, we offer flexible financing options for Clinton residents to make your stairlift or elevator more affordable. Contact us at (660) 347-2070 to discuss payment plans that fit your budget."
                  }
                }
              ]
            })}
          </script>
        </Helmet>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Frequently Asked Questions
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <img src="/images/faq1.jpg" alt="Customer service consultation" className="rounded-lg w-full h-48 object-cover" />
              <img src="/images/faq2.jpg" alt="Happy elderly couple reviewing information" className="rounded-lg w-full h-48 object-cover" />
            </div>
            
            <div className="space-y-6">
              <div className="bg-background rounded-lg p-6 border">
                <h3 className="text-xl font-bold mb-3 text-foreground">
                  How much does a stairlift cost in Clinton, MO?
                </h3>
                <p className="text-muted-foreground">
                  On average, straight stair lifts cost between{" "}
                  <strong className="text-foreground">$2,500 and $5,000</strong>, including installation. 
                  Curved custom models typically range from{" "}
                  <strong className="text-foreground">$8,500 to $15,000+</strong>{" "}
                  depending on staircase complexity. Use our calculator for an exact quote based on your home.
                </p>
              </div>

              <div className="bg-background rounded-lg p-6 border">
                <h3 className="text-xl font-bold mb-3 text-foreground">
                  What is the cost of a residential elevator vs a stairlift?
                </h3>
                <p className="text-muted-foreground">
                  Residential elevators are significantly more expensive, often starting at{" "}
                  <strong className="text-foreground">$20,000 to $50,000+</strong>{" "}
                  due to required home construction. Stair lifts are a far more affordable alternative 
                  for multi-story accessibility in Clinton homes.
                </p>
              </div>

              <div className="bg-background rounded-lg p-6 border">
                <h3 className="text-xl font-bold mb-3 text-foreground">
                  How quickly can you install a stairlift in Clinton?
                </h3>
                <p className="text-muted-foreground">
                  Straight stairlift installations typically take 2-4 hours and can often be scheduled within days. 
                  Curved stairlifts require custom rail fabrication (2-3 weeks) followed by a 4-6 hour installation. 
                  We serve Clinton and surrounding Henry County communities with prompt, professional service.
                </p>
              </div>

              <div className="bg-background rounded-lg p-6 border">
                <h3 className="text-xl font-bold mb-3 text-foreground">
                  Do you offer financing for stairlifts?
                </h3>
                <p className="text-muted-foreground">
                  Yes, we offer flexible financing options for Clinton residents to make your stairlift or elevator 
                  more affordable. Contact us at (660) 347-2070 to discuss payment plans that fit your budget.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Improve Your Home's Accessibility?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Get a free, no-obligation quote for your Clinton, MO home. Our certified specialists are ready to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <a href="tel:6603472070">
                <Phone className="w-5 h-5 mr-2" />
                Call (660) 347-2070
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild className="bg-white/20 backdrop-blur border-white text-white hover:bg-white/30">
              <NavLink to="/stair-lift-cost-calculator">Calculate Your Cost</NavLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Google Maps Embed */}
            <div className="mb-6 rounded-lg overflow-hidden border max-w-2xl mx-auto">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3119.8!2d-93.7785!3d38.3686!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDIyJzA3LjAiTiA5M8KwNDYnNDIuNiJX!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Affordable Home Stairlift - Clinton, MO Location"
              ></iframe>
            </div>
            
            <p className="text-muted-foreground mb-4">
              <strong className="text-foreground">Affordable Home Stairlift</strong> - Mobility Equipment Supplier serving Clinton, MO
            </p>
            <div className="flex items-center justify-center gap-2 text-sm mb-4">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="ml-2 text-muted-foreground">5.0 Rating | 100+ Reviews</span>
            </div>
            <p className="text-sm text-muted-foreground">
              <a href="tel:6603472070" className="text-primary hover:underline">(660) 347-2070</a>
              {" | "}
              <NavLink to="/stair-lift-cost-calculator" className="text-primary hover:underline">Stairlift Calculator</NavLink>
              {" | "}
              <NavLink to="/home-elevator-cost-calculator" className="text-primary hover:underline">Elevator Calculator</NavLink>
            </p>
            <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground mt-4">
              <a href="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</a>
              <span>•</span>
              <a href="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Local Business Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Affordable Home Stairlift - Clinton, MO",
          "description": "Mobility equipment supplier serving Clinton, MO and Henry County. Professional stairlift and residential elevator installation.",
          "url": "https://www.affordablehomestairlift.com",
          "telephone": "+16603472070",
          "areaServed": {
            "@type": "City",
            "name": "Clinton",
            "sameAs": "https://en.wikipedia.org/wiki/Clinton,_Missouri"
          },
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Clinton",
            "addressRegion": "MO",
            "postalCode": "64735",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "38.3686",
            "longitude": "-93.7785"
          },
          "priceRange": "$$",
          "image": "https://www.affordablehomestairlift.com/images/stairlift-installed.jpg"
        })
      }} />
    </div>
  );
};

export default Index;
